// # Dedicated Plan Component for Step 2 Mentorship
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import SharedHeader from '../../../components/SharedHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { 
  Box, Typography, List, ListItem,
  Tabs, Tab, Chip, Grid,
  Tooltip, Checkbox, Select,
  MenuItem, FormControl,
  InputLabel, IconButton,
  TextField, Modal, Paper
} from '@mui/material';
import { 
  Today as TodayIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Sort as SortIcon,
  FilterList as FilterIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { format } from 'date-fns';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import './dedicated2.css';

// # Define task contexts
const TASK_CONTEXTS = {
  general: { label: 'General', color: '#3498db' },
  study: { label: 'Study', color: '#2ecc71' },
  practice: { label: 'Practice', color: '#f39c12' }
};

function Dedicated2({ isAdmin }) {
  const navigate = useNavigate();
  const { userProfile, user } = useAuthStore();
  const [selectedTab, setSelectedTab] = useState(0);
  const [tasks, setTasks] = useState({});
  const [targetUserId, setTargetUserId] = useState(null);
  const [filterContext, setFilterContext] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // # Create base path based on admin status
  const basePath = isAdmin ? '/admin/mentorship' : '/mentorship';

  // # Get the correct user ID (mentee's ID when mentor is viewing)
  useEffect(() => {
    const menteeId = sessionStorage.getItem('currentViewingMenteeId');
    const userId = menteeId || user?.uid;
    setTargetUserId(userId);

    // # Load tasks from Firestore
    const loadTasks = async () => {
      if (!userId) return;

      try {
        const docRef = doc(db, 's2_mentee', userId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          // # Get dedicated_tasks data or initialize empty object
          const dedicatedTasks = data.dedicated_tasks || {};
          setTasks(dedicatedTasks);
        }
      } catch (error) {
        console.error('Error loading tasks:', error);
      }
    };

    loadTasks();
  }, [user?.uid]);

  // # Memoize handleToggleComplete to prevent unnecessary re-renders
  const handleToggleComplete = useCallback(async (taskId) => {
    if (!targetUserId) return;

    try {
      const updatedTasks = {
        ...tasks,
        [taskId]: {
          ...tasks[taskId],
          completed: !tasks[taskId].completed
        }
      };

      // # Update in Firestore
      const docRef = doc(db, 's2_mentee', targetUserId);
      await setDoc(docRef, {
        dedicated_tasks: updatedTasks
      }, { merge: true });

      // # Update local state
      setTasks(updatedTasks);
      
      // # Update selectedTask if it's the same task
      if (selectedTask && selectedTask.id === taskId) {
        setSelectedTask(prev => ({
          ...prev,
          completed: !prev.completed
        }));
      }
    } catch (error) {
      console.error('Error updating task:', error);
    }
  }, [targetUserId, tasks, selectedTask]);

  // # Memoize tasks array conversion
  const tasksArray = useMemo(() => Object.entries(tasks).map(([id, task]) => ({
    id,
    ...task
  })), [tasks]);

  // # Memoize filtered and sorted tasks
  const filteredAndSortedTasks = useMemo(() => {
    return tasksArray
      .filter(task => {
        const matchesContext = filterContext === 'all' || task.context === filterContext;
        const matchesStatus = filterStatus === 'all' || 
          (filterStatus === 'completed' ? task.completed : !task.completed);
        const matchesSearch = !searchQuery || 
          task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.description?.toLowerCase().includes(searchQuery.toLowerCase());
        
        return matchesContext && matchesStatus && matchesSearch;
      })
      .sort((a, b) => {
        let comparison = 0;
        switch (sortBy) {
          case 'date':
            comparison = new Date(a.date) - new Date(b.date);
            break;
          case 'title':
            comparison = a.title.localeCompare(b.title);
            break;
          case 'status':
            comparison = (a.completed === b.completed) ? 0 : a.completed ? 1 : -1;
            break;
          default:
            comparison = 0;
        }
        return sortOrder === 'asc' ? comparison : -comparison;
      });
  }, [tasksArray, filterContext, filterStatus, searchQuery, sortBy, sortOrder]);

  // # Memoize modal handlers
  const handleOpenModal = useCallback((task) => {
    setSelectedTask(task);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedTask(null);
    setModalOpen(false);
  }, []);

  // # Memoize filter handlers
  const handleFilterContextChange = useCallback((e) => {
    setFilterContext(e.target.value);
  }, []);

  const handleFilterStatusChange = useCallback((e) => {
    setFilterStatus(e.target.value);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleSortChange = useCallback((e) => {
    setSortBy(e.target.value);
  }, []);

  const handleSortOrderToggle = useCallback(() => {
    setSortOrder(order => order === 'asc' ? 'desc' : 'asc');
  }, []);

  // # Task Details Modal Component - Memoize to prevent unnecessary re-renders
  const TaskDetailsModal = useMemo(() => {
    if (!selectedTask) return null;

    return (
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        className="dedicated2_modal"
      >
        <Paper className="dedicated2_modal_content">
          <div className="dedicated2_modal_header">
            <div className="dedicated2_modal_title_section">
              <div className="dedicated2_modal_title_group">
                <Checkbox
                  checked={selectedTask.completed}
                  onChange={() => handleToggleComplete(selectedTask.id)}
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  className={`dedicated2_checkbox ${selectedTask.completed ? 'completed' : ''}`}
                />
                <Typography variant="h5" className="dedicated2_modal_title">
                  {selectedTask.title}
                </Typography>
              </div>
              <Chip
                label={TASK_CONTEXTS[selectedTask.context]?.label}
                className="dedicated2_context_chip"
                style={{
                  backgroundColor: TASK_CONTEXTS[selectedTask.context]?.color,
                  color: 'white'
                }}
              />
            </div>
            <IconButton 
              onClick={handleCloseModal}
              className="dedicated2_modal_close"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div className="dedicated2_modal_body">
            <div className="dedicated2_modal_section">
              <Typography variant="subtitle2" className="dedicated2_modal_section_title">
                Date
              </Typography>
              <div className="dedicated2_modal_date">
                <TodayIcon fontSize="small" />
                <Typography>
                  {format(new Date(selectedTask.date), 'MMMM dd, yyyy')}
                </Typography>
              </div>
            </div>

            {selectedTask.description && (
              <div className="dedicated2_modal_section">
                <Typography variant="subtitle2" className="dedicated2_modal_section_title">
                  Description
                </Typography>
                <Typography className="dedicated2_modal_description">
                  {selectedTask.description}
                </Typography>
              </div>
            )}

            <div className="dedicated2_modal_section">
              <Typography variant="subtitle2" className="dedicated2_modal_section_title">
                Details
              </Typography>
              <div className="dedicated2_modal_tags">
                {selectedTask.chapter && (
                  <div className="dedicated2_modal_tag_group">
                    <Typography variant="body2" color="textSecondary">
                      Chapter:
                    </Typography>
                    <Chip
                      label={selectedTask.chapter}
                      className="dedicated2_chapter_chip"
                      variant="outlined"
                      size="small"
                    />
                  </div>
                )}
                {selectedTask.qbank && (
                  <div className="dedicated2_modal_tag_group">
                    <Typography variant="body2" color="textSecondary">
                      QBank:
                    </Typography>
                    <Chip
                      label={selectedTask.qbank}
                      className="dedicated2_qbank_chip"
                      variant="outlined"
                      size="small"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="dedicated2_modal_section">
              <Typography variant="subtitle2" className="dedicated2_modal_section_title">
                Status
              </Typography>
              <Chip
                label={selectedTask.completed ? 'Completed' : 'Pending'}
                className={`dedicated2_status_chip ${selectedTask.completed ? 'completed' : 'pending'}`}
              />
            </div>
          </div>
        </Paper>
      </Modal>
    );
  }, [selectedTask, modalOpen, handleCloseModal]);

  return (
    <div className={`dedicated2_container ${selectedTask ? 'modal-open' : ''}`}>
      <SharedHeader />
      <div className="dedicated2_main">
        <div className="dedicated2_content">
          <div className="dedicated2_header">
            <Typography variant="h4" component="h1" className="dedicated2_title">
              Dedicated Period Planning
            </Typography>
          </div>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={selectedTab}
              onChange={(e, newValue) => setSelectedTab(newValue)}
              aria-label="dedicated tasks tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Tasks List" />
              <Tab label="Calendar View" />
            </Tabs>
          </Box>

          {/* Filter Controls */}
          <div className="dedicated2_filter_controls">
            <div className="dedicated2_filter_group">
              <TextField
                size="small"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="dedicated2_search_input"
              />
              <FormControl size="small" className="dedicated2_filter_select">
                <InputLabel>Context</InputLabel>
                <Select
                  value={filterContext}
                  onChange={handleFilterContextChange}
                  label="Context"
                >
                  <MenuItem value="all">All Contexts</MenuItem>
                  {Object.entries(TASK_CONTEXTS).map(([key, { label }]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" className="dedicated2_filter_select">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={handleFilterStatusChange}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </div>
            <div className="dedicated2_sort_group">
              <FormControl size="small" className="dedicated2_filter_select">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={handleSortChange}
                  label="Sort By"
                >
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="title">Title</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title={`Sort ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}>
                <IconButton 
                  onClick={handleSortOrderToggle}
                  className={`dedicated2_sort_button ${sortOrder === 'desc' ? 'reversed' : ''}`}
                  size="small"
                >
                  <SortIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {/* Tasks List View */}
          {selectedTab === 0 && (
            <div className="dedicated2_tasks_container">
              <Grid 
                container 
                spacing={2}
                sx={{
                  width: '100%',
                  margin: '0',
                  boxSizing: 'border-box'
                }}
              >
                {filteredAndSortedTasks.length > 0 ? (
                  filteredAndSortedTasks.map((task) => (
                    <Grid 
                      item 
                      xs={12} 
                      sm={6} 
                      md={4} 
                      key={task.id}
                      sx={{
                        paddingTop: '16px',
                        paddingBottom: '16px'
                      }}
                    >
                      <div className="dedicated2_task_card">
                        <div className="dedicated2_task_header">
                          <div className="dedicated2_task_header_left">
                            <Checkbox
                              checked={task.completed}
                              onChange={() => handleToggleComplete(task.id)}
                              icon={<RadioButtonUncheckedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                              className={`dedicated2_checkbox ${task.completed ? 'completed' : ''}`}
                            />
                            <Typography variant="h6" className="dedicated2_task_title">
                              {task.title}
                            </Typography>
                            <IconButton
                              size="small"
                              className="dedicated2_expand_button"
                              onClick={() => handleOpenModal(task)}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </div>
                          <Chip
                            label={TASK_CONTEXTS[task.context]?.label}
                            className="dedicated2_context_chip"
                            style={{
                              backgroundColor: TASK_CONTEXTS[task.context]?.color,
                              color: 'white'
                            }}
                          />
                        </div>
                        <div className="dedicated2_task_date">
                          <TodayIcon fontSize="small" />
                          <Typography variant="body2">
                            {format(new Date(task.date), 'MMM dd, yyyy')}
                          </Typography>
                        </div>
                        {task.description && (
                          <Typography className="dedicated2_task_description">
                            {task.description}
                          </Typography>
                        )}
                        <div className="dedicated2_task_tags">
                          {task.chapter && (
                            <Chip
                              label={task.chapter}
                              className="dedicated2_chapter_chip"
                              variant="outlined"
                              size="small"
                            />
                          )}
                          {task.qbank && (
                            <Chip
                              label={task.qbank}
                              className="dedicated2_qbank_chip"
                              variant="outlined"
                              size="small"
                            />
                          )}
                        </div>
                      </div>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Typography className="dedicated2_empty_message">
                      No tasks found matching your filters.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </div>
          )}

          {/* Calendar View */}
          {selectedTab === 1 && (
            <div className="dedicated2_calendar_section">
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,listWeek'
                }}
                events={filteredAndSortedTasks.map(task => ({
                  id: task.id,
                  title: task.title,
                  start: task.date,
                  allDay: true,
                  backgroundColor: task.completed ? '#d4edda' : '#f8d7da',
                  borderColor: task.completed ? '#c3e6cb' : '#f5c6cb',
                  textColor: '#333333',
                  extendedProps: {
                    description: task.description,
                    context: task.context,
                    chapter: task.chapter,
                    qbank: task.qbank,
                    completed: task.completed
                  }
                }))}
                height="auto"
                aspectRatio={1.5}
                handleWindowResize={true}
                stickyHeaderDates={true}
                dayMaxEvents={true}
                views={{
                  dayGridMonth: {
                    dayMaxEvents: 3,
                  },
                  timeGridWeek: {
                    dayMaxEvents: true,
                  },
                  listWeek: {
                    dayMaxEvents: true,
                  }
                }}
                eventContent={(eventInfo) => (
                  <Tooltip 
                    title={`${eventInfo.event.title} - ${TASK_CONTEXTS[eventInfo.event.extendedProps.context]?.label}`}
                    arrow 
                    placement="top"
                  >
                    <div className="dedicated2_calendar_event">
                      <Checkbox
                        checked={eventInfo.event.extendedProps.completed}
                        onChange={() => handleToggleComplete(eventInfo.event.id)}
                        icon={<RadioButtonUncheckedIcon fontSize="small" />}
                        checkedIcon={<CheckCircleIcon fontSize="small" />}
                        className={`dedicated2_calendar_checkbox ${eventInfo.event.extendedProps.completed ? 'completed' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="dedicated2_calendar_event_title">
                        {eventInfo.event.title}
                      </span>
                      <IconButton
                        size="small"
                        className="dedicated2_calendar_expand_button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const task = {
                            id: eventInfo.event.id,
                            title: eventInfo.event.title,
                            date: eventInfo.event.start,
                            ...eventInfo.event.extendedProps
                          };
                          handleOpenModal(task);
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </Tooltip>
                )}
                dayMaxEventRows={false}
                firstDay={1}
                nowIndicator={true}
                weekNumbers={true}
                weekNumberFormat={{ week: 'numeric' }}
                weekText="W"
                expandRows={true}
                fixedWeekCount={false}
              />
            </div>
          )}
        </div>
      </div>
      <DashboardFooter />

      {/* Render Modal */}
      {TaskDetailsModal}
    </div>
  );
}

export default React.memo(Dedicated2);
